import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Schedule from './Schedule';
import VideoTest from './VideoTest';
import StudentMaterials, { StudentMaterialsWidget } from './StudentMaterials';
import Placeholder from '../components/Placeholder';
import LessonsList from '../components/LessonsList';
import { getUserDisplayName } from '../utils/userUtils';
import {
  formatUTCDate,
  parseUTCDate,
  isSameUTCDay,
  formatUTCDateTime,
  formatUTCTime,
} from '../utils/scheduleUtils';
import './Dashboard.css';

const formatLessonDate = (iso) =>
  formatUTCDateTime(new Date(iso), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const getLessonPartnerName = (lesson, userType) => {
  if (userType === 'teacher') {
    return lesson.student_name || lesson.student_username || '—';
  }
  return lesson.teacher_name || lesson.teacher_username || '—';
};

const StatisticsPanel = ({ statistics, userType }) => {
  const partnerLabel = userType === 'teacher' ? 'Учеников' : 'Преподавателей';
  const monthDelta = statistics.lessons_this_month - statistics.lessons_prev_month;

  return (
    <div className="statistics-page">
      <h2 className="history-title">Статистика</h2>
      <div className="statistics-grid">
        <div className="stat-card stat-card--primary">
          <div className="stat-card-value">{statistics.total_lessons}</div>
          <div className="stat-card-label">Проведено уроков</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-value">{statistics.lessons_this_month}</div>
          <div className="stat-card-label">
            В {statistics.current_month_name || 'этом месяце'}
          </div>
          {statistics.lessons_prev_month > 0 && (
            <div className={`stat-card-hint ${monthDelta >= 0 ? 'positive' : 'negative'}`}>
              {monthDelta >= 0 ? '+' : ''}{monthDelta} к прошлому месяцу
            </div>
          )}
        </div>
        <div className="stat-card">
          <div className="stat-card-value">{statistics.total_hours || 0}</div>
          <div className="stat-card-label">Часов занятий</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-value">{statistics.unique_partners || 0}</div>
          <div className="stat-card-label">{partnerLabel}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-value">{statistics.upcoming_lessons || 0}</div>
          <div className="stat-card-label">Запланировано</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-value">{statistics.cancelled_lessons || 0}</div>
          <div className="stat-card-label">Отменено</div>
        </div>
      </div>

      {statistics.recent_completed?.length > 0 && (
        <div className="statistics-recent">
          <h3 className="statistics-recent-title">Последние уроки</h3>
          <ul className="recent-lessons-list">
            {statistics.recent_completed.map((lesson) => (
              <li key={lesson.id} className="recent-lesson-item">
                <span className="recent-lesson-partner">
                  {getLessonPartnerName(lesson, userType)}
                </span>
                <span className="recent-lesson-date">
                  {formatLessonDate(lesson.started_at)}
                </span>
                {lesson.duration_minutes && (
                  <span className="recent-lesson-duration">
                    {lesson.duration_minutes} мин
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Компонент календаря для студента
const StudentCalendar = ({ lessons, onLessonUpdate }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [showLessonModal, setShowLessonModal] = useState(false);
  
  const monthNames = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
  ];
  
  const dayNames = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
  
  const getDaysInMonth = (date) => {
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const firstDay = new Date(Date.UTC(year, month, 1));
    const lastDay = new Date(Date.UTC(year, month + 1, 0));
    const daysInMonth = lastDay.getUTCDate();
    const startingDayOfWeek = (firstDay.getUTCDay() + 6) % 7; // Понедельник = 0

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(Date.UTC(year, month, day)));
    }

    return days;
  };

  const getLessonsForDate = (date) => {
    if (!date) return [];
    const dateStr = formatUTCDate(date);
    return lessons.filter((lesson) => {
      const lessonDateStr = formatUTCDate(new Date(lesson.scheduled_at));
      return lessonDateStr === dateStr;
    });
  };

  const changeMonth = (delta) => {
    const newMonth = new Date(currentMonth);
    newMonth.setUTCMonth(newMonth.getUTCMonth() + delta);
    setCurrentMonth(newMonth);
  };
  
  const goToToday = () => {
    setCurrentMonth(new Date());
  };
  
  const days = getDaysInMonth(currentMonth);
  
  return (
    <div className="student-calendar">
      <div className="calendar-header">
        <button className="calendar-nav-btn" onClick={() => changeMonth(-1)}>‹</button>
        <div className="calendar-month-year">
          <span className="calendar-month">{monthNames[currentMonth.getUTCMonth()]}</span>
          <span className="calendar-year">{currentMonth.getUTCFullYear()}</span>
        </div>
        <button className="calendar-nav-btn" onClick={() => changeMonth(1)}>›</button>
        <button className="calendar-today-btn" onClick={goToToday}>Сегодня</button>
      </div>
      
      <div className="calendar-grid">
        {/* Заголовки дней недели */}
        {dayNames.map(dayName => (
          <div key={dayName} className="calendar-day-header">{dayName}</div>
        ))}
        
        {/* Дни месяца */}
        {days.map((date, index) => {
          const isToday = date && isSameUTCDay(date, new Date());
          const dateLessons = getLessonsForDate(date);
          
          return (
            <div 
              key={index} 
              className={`calendar-day-cell ${!date ? 'empty' : ''} ${isToday ? 'today' : ''}`}
            >
              {date && (
                <>
                  <div className="calendar-day-number">{date.getUTCDate()}</div>
                  {dateLessons.length > 0 && (
                    <div className="calendar-day-lessons">
                      {dateLessons.map(lesson => {
                        const lessonDate = new Date(lesson.scheduled_at);
                        const isCompleted = lesson.status === 'completed';
                        const isCancelled = lesson.status === 'cancelled';
                        const isPast = lessonDate < new Date();
                        
                        const isFuture = !isPast && !isCompleted && !isCancelled;
                        
                        return (
                          <div
                            key={lesson.id}
                            className={`calendar-day-lesson ${isPast ? 'past' : 'future'} ${isCompleted ? 'completed' : ''} ${isCancelled ? 'cancelled' : ''} clickable`}
                            title={`${formatUTCTime(lessonDate, { hour: '2-digit', minute: '2-digit' })} - ${lesson.teacher_name || lesson.teacher_username}`}
                            onClick={() => {
                              setSelectedLesson(lesson);
                              setShowLessonModal(true);
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            <span className="lesson-time-small">
                              {formatUTCTime(lessonDate, { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
      
      {lessons.length === 0 && (
        <div className="calendar-empty">
          <p>Нет уроков</p>
        </div>
      )}
      
      {showLessonModal && selectedLesson && (
        <StudentLessonModal
          lesson={selectedLesson}
          onClose={() => {
            setShowLessonModal(false);
            setSelectedLesson(null);
          }}
          onCancel={() => {
            setShowLessonModal(false);
            setSelectedLesson(null);
            if (onLessonUpdate) {
              onLessonUpdate();
            }
          }}
        />
      )}
    </div>
  );
};

// Модальное окно урока для студента
const StudentLessonModal = ({ lesson, onClose, onCancel }) => {
  const [cancelling, setCancelling] = useState(false);

  const handleCancel = async () => {
    if (!window.confirm('Вы уверены, что хотите отменить этот урок?')) {
      return;
    }

    setCancelling(true);
    try {
      await api.patch(`/lessons/lessons/${lesson.id}/`, {
        status: 'cancelled'
      });
      alert('Урок отменен');
      onCancel();
    } catch (error) {
      console.error('Ошибка отмены урока:', error);
      alert('Ошибка при отмене урока');
    } finally {
      setCancelling(false);
    }
  };

  const lessonDate = new Date(lesson.scheduled_at);

  return (
    <div className="lesson-modal-overlay" onClick={onClose}>
      <div className="lesson-modal" onClick={(e) => e.stopPropagation()}>
        <div className="lesson-modal-header">
          <h3>Информация об уроке</h3>
          <button className="lesson-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="lesson-modal-content">
          <div className="lesson-info-item">
            <span className="lesson-info-label">Преподаватель:</span>
            <span className="lesson-info-value">{lesson.teacher_name || lesson.teacher_username}</span>
          </div>
          
          <div className="lesson-info-item">
            <span className="lesson-info-label">Дата и время:</span>
            <span className="lesson-info-value">
              {formatUTCDateTime(lessonDate, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
          
          <div className="lesson-info-item">
            <span className="lesson-info-label">Статус:</span>
            <span className="lesson-info-value">
              {lesson.status === 'scheduled' ? 'Запланирован' : 
               lesson.status === 'completed' ? 'Состоялся' : 
               lesson.status === 'cancelled' ? 'Отменен' : 
               lesson.status}
            </span>
          </div>
          
          {lesson.duration_minutes && (
            <div className="lesson-info-item">
              <span className="lesson-info-label">Длительность:</span>
              <span className="lesson-info-value">{lesson.duration_minutes} минут</span>
            </div>
          )}
        </div>

        <div className="lesson-modal-actions">
          {lesson.status === 'scheduled' && (
            <button
              className="btn-cancel-lesson"
              onClick={handleCancel}
              disabled={cancelling}
            >
              {cancelling ? 'Отмена...' : 'Отменить урок'}
            </button>
          )}
          <button className="btn-close-modal" onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Определяем активный пункт меню из URL
  const getActiveMenuItem = () => {
    const path = location.pathname;
    if (path === '/dashboard' || path === '/dashboard/home') return 'home';
    if (path.includes('/dashboard/schedule')) return 'schedule';
    if (path.includes('/dashboard/lessons')) return 'lessons';
    if (path.includes('/dashboard/students')) return 'students';
    if (path.includes('/dashboard/finance')) return 'finance';
    if (path.includes('/dashboard/video-test')) return 'video-test';
    if (path.includes('/dashboard/materials')) return 'materials';
    if (path.includes('/dashboard/statistics')) return 'statistics';
    return 'home';
  };
  
  const activeMenuItem = getActiveMenuItem();
  const [lessonHistory, setLessonHistory] = useState([]);
  const [statistics, setStatistics] = useState({
    total_lessons: 0,
    lessons_this_month: 0,
    lessons_prev_month: 0,
    current_month_name: '',
    total_minutes: 0,
    total_hours: 0,
    unique_partners: 0,
    cancelled_lessons: 0,
    upcoming_lessons: 0,
    recent_completed: [],
  });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [nextLesson, setNextLesson] = useState(null);
  const [nextLessonLoading, setNextLessonLoading] = useState(false);
  const [futureLessons, setFutureLessons] = useState([]);
  const [futureLessonsLoading, setFutureLessonsLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    
    if (user.user_type === 'teacher') {
      fetchStudents();
      fetchStatistics();
      fetchNextLesson();
    } else if (user.user_type === 'student') {
      fetchTeachers();
      fetchStatistics();
      fetchNextLesson();
      fetchFutureLessons();
    }
  }, [user]);


  useEffect(() => {
    if (activeMenuItem === 'lessons' && user) {
      fetchLessonHistory();
    }
  }, [activeMenuItem, user]);

  const fetchTeachers = async () => {
    try {
      const response = await api.get('/auth/teachers/');
      setUsers(response.data);
    } catch (error) {
      console.error('Ошибка загрузки преподавателей:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await api.get('/auth/students/');
      setUsers(response.data);
    } catch (error) {
      console.error('Ошибка загрузки учеников:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnterClassroom = (userId) => {
    const teacherId = user.user_type === 'teacher' ? user.id : userId;
    navigate(`/classroom/${teacherId}`);
  };

  const fetchLessonHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await api.get('/lessons/history/');
      setLessonHistory(response.data);
    } catch (error) {
      console.error('Ошибка загрузки истории уроков:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/lessons/history/statistics/');
      setStatistics(response.data);
    } catch (error) {
      console.error('Ошибка загрузки статистики:', error);
    }
  };

  const fetchNextLesson = async () => {
    setNextLessonLoading(true);
    try {
      const response = await api.get('/lessons/lessons/next/');
      console.log('Next lesson response:', response.data);
      if (response.data && response.data.id && !response.data.message) {
        setNextLesson(response.data);
      } else {
        console.log('No lesson data in response:', response.data);
        setNextLesson(null);
      }
    } catch (error) {
      console.error('Ошибка загрузки следующего урока:', error);
      setNextLesson(null);
    } finally {
      setNextLessonLoading(false);
    }
  };

  const fetchFutureLessons = async () => {
    setFutureLessonsLoading(true);
    try {
      // Получаем все уроки (будущие, прошедшие и отмененные)
      const response = await api.get('/lessons/lessons/');
      // Получаем все уроки
      const allLessons = response.data.results || response.data || [];
      // Сортируем по дате (сначала будущие, потом прошедшие)
      allLessons.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
      setFutureLessons(allLessons);
    } catch (error) {
      console.error('Ошибка загрузки уроков:', error);
      setFutureLessons([]);
    } finally {
      setFutureLessonsLoading(false);
    }
  };

  const handleEnterLesson = () => {
    if (!nextLesson) return;
    
    // Проверяем доступность урока
    if (!nextLesson.is_accessible) {
      alert('Вход на урок доступен только за 10 минут до начала и до окончания урока');
      return;
    }
    
    // Определяем teacherId для перехода в Classroom
    // Для учителя используем свой ID, для ученика - ID учителя из урока
    const teacherId = user.user_type === 'teacher' ? user.id : nextLesson.teacher;
    navigate(`/classroom/${teacherId}`);
  };

  // Для учителя показываем новый дизайн
  if (user && user.user_type === 'teacher') {
    const getGreeting = () => {
      const hour = new Date().getUTCHours();
      if (hour >= 5 && hour < 12) return 'Доброе утро';
      if (hour >= 12 && hour < 18) return 'Добрый день';
      return 'Добрый вечер';
    };

    return (
      <div className="teacher-dashboard">
        <Header />
        <div className="dashboard-layout">
          {/* Боковая панель навигации */}
          <aside className="dashboard-sidebar">
            <nav className="sidebar-nav">
              <Link 
                to="/dashboard"
                className={`nav-item ${activeMenuItem === 'home' ? 'active' : ''}`}
              >
                <span className="nav-text">Главная</span>
              </Link>
              <Link 
                to="/dashboard/schedule"
                className={`nav-item ${activeMenuItem === 'schedule' ? 'active' : ''}`}
              >
                <span className="nav-text">Расписание</span>
              </Link>
              <Link 
                to="/dashboard/lessons"
                className={`nav-item ${activeMenuItem === 'lessons' ? 'active' : ''}`}
              >
                <span className="nav-text">Уроки</span>
              </Link>
              <Link 
                to="/dashboard/students"
                className={`nav-item ${activeMenuItem === 'students' ? 'active' : ''}`}
              >
                <span className="nav-text">Ученики</span>
              </Link>
              <Link 
                to="/dashboard/finance"
                className={`nav-item ${activeMenuItem === 'finance' ? 'active' : ''}`}
              >
                <span className="nav-text">Финансы</span>
              </Link>
            </nav>

            <div className="sidebar-divider"></div>

            <div className="sidebar-section">
              <div className="sidebar-section-title">ДОПОЛНИТЕЛЬНО</div>
              <Link 
                to="/dashboard/video-test"
                className={`nav-item ${activeMenuItem === 'video-test' ? 'active' : ''}`}
              >
                <span className="nav-text">Тест видеосвязи</span>
              </Link>
              <Link 
                to="/dashboard/statistics"
                className={`nav-item ${activeMenuItem === 'statistics' ? 'active' : ''}`}
              >
                <span className="nav-text">Статистика</span>
              </Link>
            </div>
          </aside>

          {/* Основной контент */}
          <main className={`dashboard-content${activeMenuItem === 'schedule' ? ' dashboard-content--schedule' : ''}`}>
            {activeMenuItem === 'home' && (
            <div className="dashboard-header">
                <span className="greeting">{getGreeting()}, {getUserDisplayName(user)}</span>
            </div>
            )}

            {activeMenuItem === 'schedule' ? (
              <Schedule />
            ) : activeMenuItem === 'video-test' ? (
              <VideoTest />
            ) : activeMenuItem === 'lessons' ? (
              <div className="lesson-history-container">
                <h2 className="history-title">Уроки</h2>
                <LessonsList
                  lessons={lessonHistory}
                  loading={historyLoading}
                  userType={user.user_type}
                />
              </div>
            ) : activeMenuItem === 'students' ? (
              <Placeholder title="Ученики" />
            ) : activeMenuItem === 'finance' ? (
              <Placeholder title="Финансы" />
            ) : activeMenuItem === 'statistics' ? (
              <StatisticsPanel statistics={statistics} userType="teacher" />
            ) : (
            <div className="dashboard-widgets">
              {/* Виджет следующего урока */}
              <div className="widget widget-next-lesson">
                <h3 className="widget-title">Следующий урок</h3>
                <div className="next-lesson-info">
                  {nextLessonLoading ? (
                    <div className="no-lesson">Загрузка...</div>
                  ) : nextLesson ? (
                    <>
                  <div className="lesson-student">
                        <div className="student-name">
                          {user.user_type === 'teacher' 
                            ? (nextLesson.student_name || nextLesson.student_username)
                            : (nextLesson.teacher_name || nextLesson.teacher_username)
                          }
                        </div>
                        <div className="lesson-time">
                          {formatUTCDateTime(new Date(nextLesson.scheduled_at), {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                        {!nextLesson.is_accessible && (
                          <div className="lesson-status">
                            {new Date(nextLesson.scheduled_at) > new Date()
                              ? 'Вход будет доступен за 10 минут до начала'
                              : 'Урок уже завершен'
                            }
                          </div>
                    )}
                  </div>
                  <div className="lesson-actions">
                    <button 
                          className={`btn-widget btn-primary ${!nextLesson.is_accessible ? 'disabled' : ''}`}
                          onClick={handleEnterLesson}
                          disabled={!nextLesson.is_accessible}
                    >
                          Войти на урок
                    </button>
                        <button 
                          className="btn-widget btn-secondary"
                          onClick={() => navigate('/dashboard/schedule')}
                        >
                      Расписание
                    </button>
                  </div>
                    </>
                  ) : (
                    <div className="no-lesson">Нет запланированных уроков</div>
                  )}
                </div>
              </div>

              {/* Виджет оповещений */}
              <div className="widget widget-notifications">
                <h3 className="widget-title">Оповещения</h3>
                <div className="notification-content">
                  <div className="notification-question">
                    Как видят вас новички в школе?
                  </div>
                  <div className="notification-links">
                    <a href="#" className="notification-link">Посмотрите свой профиль</a>
                    <span> или </span>
                    <a href="#" className="notification-link" onClick={() => navigate('/settings')}>
                      Измените данные о себе
                    </a>
                  </div>
                  <div className="notification-emoji">👤</div>
                </div>
              </div>

              {/* Виджет KPI */}
              <div className="widget widget-kpi">
                <h3 className="widget-title">KPI</h3>
                <div className="kpi-list">
                  <div className="kpi-item">
                    <div className="kpi-label">1st Lesson Discipline</div>
                    <div className="kpi-progress">
                      <div className="kpi-progress-bar" style={{ width: '0%' }}></div>
                    </div>
                  </div>
                  <div className="kpi-item">
                    <div className="kpi-label">Lifetime</div>
                    <div className="kpi-progress">
                      <div className="kpi-progress-bar" style={{ width: '0%' }}></div>
                    </div>
                  </div>
                  <div className="kpi-item">
                    <div className="kpi-label">Performance New</div>
                    <div className="kpi-progress">
                      <div className="kpi-progress-bar" style={{ width: '0%' }}></div>
                    </div>
                  </div>
                  <div className="kpi-item">
                    <div className="kpi-label">Retention</div>
                    <div className="kpi-progress">
                      <div className="kpi-progress-bar" style={{ width: '0%' }}></div>
                    </div>
                  </div>
                  <div className="kpi-item">
                    <div className="kpi-label">Intensity Excl. New</div>
                    <div className="kpi-progress">
                      <div className="kpi-progress-bar" style={{ width: '0%' }}></div>
                    </div>
                  </div>
                  <div className="kpi-item">
                    <div className="kpi-label">Teacher Discipline</div>
                    <div className="kpi-progress">
                      <div className="kpi-progress-bar" style={{ width: '0%' }}></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Виджет статистики за месяц */}
              <div className="widget widget-stats">
                <h3 className="widget-title">
                  Вы провели в {statistics.current_month_name || 'этом месяце'}
                </h3>
                <div className="stats-content">
                  <div className="stats-number">{statistics.lessons_this_month} уроков</div>
                  <div className="stats-message">
                    {statistics.lessons_prev_month > 0
                      ? statistics.lessons_this_month >= statistics.lessons_prev_month
                        ? `+${statistics.lessons_this_month - statistics.lessons_prev_month} к прошлому месяцу`
                        : `${statistics.lessons_this_month - statistics.lessons_prev_month} к прошлому месяцу`
                      : 'Продолжайте в том же духе'}
                  </div>
                </div>
              </div>

              <div className="widget widget-total-stats">
                <h3 className="widget-title">Всего на RoseLearn.me</h3>
                <div className="stats-content">
                  <div className="stats-number-large">{statistics.total_lessons} уроков</div>
                  <div className="stats-detail-row">
                    {statistics.total_hours > 0 && (
                      <span>{statistics.total_hours} ч занятий</span>
                    )}
                    {statistics.unique_partners > 0 && (
                      <span>{statistics.unique_partners} учеников</span>
                    )}
                  </div>
                  {statistics.upcoming_lessons > 0 && (
                    <div className="stats-message">
                      Запланировано: {statistics.upcoming_lessons}
                    </div>
                  )}
                </div>
              </div>
            </div>
            )}
          </main>
        </div>
        <Footer />
      </div>
    );
  }

  // Для студентов показываем новый дашборд
  if (user && user.user_type === 'student') {
    const getGreeting = () => {
      const hour = new Date().getUTCHours();
      if (hour >= 5 && hour < 12) return 'Доброе утро';
      if (hour >= 12 && hour < 18) return 'Добрый день';
      return 'Добрый вечер';
    };

    return (
      <div className="teacher-dashboard">
        <Header />
        <div className="dashboard-layout">
          {/* Боковая панель навигации */}
          <aside className="dashboard-sidebar">
            <nav className="sidebar-nav">
              <Link 
                to="/dashboard"
                className={`nav-item ${activeMenuItem === 'home' ? 'active' : ''}`}
              >
                <span className="nav-text">Главная</span>
              </Link>
              <Link 
                to="/dashboard/lessons"
                className={`nav-item ${activeMenuItem === 'lessons' ? 'active' : ''}`}
              >
                <span className="nav-text">Уроки</span>
              </Link>
            </nav>

            <div className="sidebar-divider"></div>

            <div className="sidebar-section">
              <div className="sidebar-section-title">ДОПОЛНИТЕЛЬНО</div>
              <Link 
                to="/dashboard/video-test"
                className={`nav-item ${activeMenuItem === 'video-test' ? 'active' : ''}`}
              >
                <span className="nav-text">Проверить видеосвязь</span>
              </Link>
            </div>
          </aside>

          {/* Основной контент */}
          <main className="dashboard-content">
            {activeMenuItem === 'home' && (
              <div className="dashboard-header">
                <span className="greeting">{getGreeting()}, {getUserDisplayName(user)}</span>
              </div>
            )}

            {activeMenuItem === 'video-test' ? (
              <VideoTest />
            ) : activeMenuItem === 'materials' ? (
              <StudentMaterials />
            ) : activeMenuItem === 'lessons' ? (
              <div className="lesson-history-container">
                <h2 className="history-title">Уроки</h2>
                <LessonsList
                  lessons={lessonHistory}
                  loading={historyLoading}
                  userType={user.user_type}
                />
              </div>
            ) : (
            <div className="dashboard-widgets">
              {/* Виджет следующего урока */}
              <div className="widget widget-next-lesson">
                <h3 className="widget-title">Следующий урок</h3>
                <div className="next-lesson-info">
                  {nextLessonLoading ? (
                    <div className="no-lesson">Загрузка...</div>
                  ) : nextLesson ? (
                    <>
                      <div className="lesson-student">
                        <div className="student-name">
                          {nextLesson.teacher_name || nextLesson.teacher_username}
                        </div>
                        <div className="lesson-time">
                          {formatUTCDateTime(new Date(nextLesson.scheduled_at), {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                        {!nextLesson.is_accessible && (
                          <div className="lesson-status">
                            {new Date(nextLesson.scheduled_at) > new Date()
                              ? 'Вход будет доступен за 10 минут до начала'
                              : 'Урок уже завершен'
                            }
                          </div>
                        )}
                      </div>
                      <div className="lesson-actions">
                        <button 
                          className={`btn-widget btn-primary ${!nextLesson.is_accessible ? 'disabled' : ''}`}
                          onClick={handleEnterLesson}
                          disabled={!nextLesson.is_accessible}
                        >
                          Войти на урок
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="no-lesson">
                      <p>Нет запланированных уроков</p>
                      <button 
                        className="btn-widget btn-primary"
                        onClick={() => navigate('/teachers')}
                        style={{ marginTop: '1rem' }}
                      >
                        Добавить
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Виджет пройденных уроков */}
              <div className="widget widget-completed-lessons">
                <h3 className="widget-title">Пройденные уроки</h3>
                <div className="completed-lessons-content">
                  <div className="progress-stats">
                    <div className="progress-item">
                      <div className="progress-label">Всего уроков</div>
                      <div className="progress-value">{statistics.total_lessons || 0}</div>
                    </div>
                    <div className="progress-item">
                      <div className="progress-label">
                        В {statistics.current_month_name || 'этом месяце'}
                      </div>
                      <div className="progress-value">{statistics.lessons_this_month || 0}</div>
                    </div>
                    {(statistics.total_hours > 0) && (
                      <div className="progress-item">
                        <div className="progress-label">Часов занятий</div>
                        <div className="progress-value">{statistics.total_hours}</div>
                      </div>
                    )}
                  </div>
                  {statistics.recent_completed?.length > 0 ? (
                    <ul className="recent-lessons-list recent-lessons-list--compact">
                      {statistics.recent_completed.slice(0, 3).map((lesson) => (
                        <li key={lesson.id} className="recent-lesson-item">
                          <span className="recent-lesson-partner">
                            {getLessonPartnerName(lesson, 'student')}
                          </span>
                          <span className="recent-lesson-meta">
                            {formatLessonDate(lesson.started_at)}
                            {lesson.duration_minutes
                              ? ` · ${lesson.duration_minutes} мин`
                              : ''}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="completed-lessons-empty">
                      Завершённые уроки появятся после проведения занятий
                    </p>
                  )}
                  <Link to="/dashboard/lessons" className="completed-lessons-link">
                    Все уроки →
                  </Link>
                </div>
              </div>

              {/* Виджет учебных материалов */}
              <div className="widget widget-materials">
                <h3 className="widget-title">Учебные материалы</h3>
                <div className="materials-content">
                  <StudentMaterialsWidget />
                </div>
              </div>

              {/* Виджет проверки видеосвязи */}
              <div className="widget widget-video-check">
                <h3 className="widget-title">Проверить видеосвязь</h3>
                <div className="video-check-content">
                  <p className="video-check-description">
                    Убедитесь, что ваша камера и микрофон работают корректно
                  </p>
                  <button 
                    className="btn-widget btn-primary"
                    onClick={() => navigate('/dashboard/video-test')}
                  >
                    Проверить
                  </button>
                </div>
              </div>

              {/* Виджет календаря */}
              <div className="widget widget-calendar">
                <h3 className="widget-title">Календарь</h3>
                <div className="calendar-content">
                  {futureLessonsLoading ? (
                    <div className="calendar-loading">Загрузка...</div>
                  ) : (
                    <StudentCalendar 
                      lessons={futureLessons}
                      onLessonUpdate={fetchFutureLessons} 
                      onLessonUpdate={fetchFutureLessons}
                    />
                  )}
                  <button 
                    className="btn-widget btn-primary calendar-add-btn"
                    onClick={() => navigate('/teachers')}
                    style={{ marginTop: '1rem', width: '100%' }}
                  >
                    Добавить урок
                  </button>
                </div>
              </div>

            </div>
            )}
          </main>
        </div>
        <Footer />
      </div>
    );
  }

  // Fallback для других случаев
  return (
    <div className="dashboard">
      <Header pageTitle="Личный кабинет" />
      <main className="dashboard-main">
        <div className="dashboard-container">
          <h2 className="page-title">Личный кабинет</h2>
          <h3>Выберите преподавателя</h3>
          {loading ? (
            <div>Загрузка...</div>
          ) : users.length === 0 ? (
            <div>Нет доступных преподавателей</div>
          ) : (
            <div className="teachers-grid">
              {users.map((person) => (
                <div key={person.id} className="teacher-card">
                  <h3>{person.first_name || ''} {person.last_name || ''}</h3>
                  <p className="teacher-username">@{person.username}</p>
                  {person.bio && <p className="teacher-bio">{person.bio}</p>}
                  <button
                    onClick={() => handleEnterClassroom(person.id)}
                    className="btn-enter-classroom"
                  >
                    Войти в класс
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Dashboard;
